import axios, { AxiosError } from "axios";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:3010";

/**
 * Storage keys are prefixed differently from the admin panel on purpose.
 *
 * If both panels are ever served from the same origin, sharing a key would let
 * a customer session and an operator session overwrite one another.
 */
export const storage = {
  accessToken: "cps.accessToken",
  email: "cps.email",
} as const;

export const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(storage.accessToken);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export function clearSession() {
  Object.values(storage).forEach((key) => localStorage.removeItem(key));
}

/**
 * No refresh flow here, unlike the admin panel.
 *
 * The operator token is short-lived by design and there is no refresh token to
 * rotate: an unattended session that silently renews itself for weeks is a
 * worse trade for a panel that can see every tenant. A 401 simply signs out.
 */
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const url = error.config?.url ?? "";
    if (error.response?.status === 401 && !url.includes("/system/login")) {
      clearSession();
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

export function apiErrorCode(error: unknown): string | null {
  if (!axios.isAxiosError(error)) return null;
  const payload = error.response?.data as { error?: { code?: string } } | undefined;
  return payload?.error?.code ?? null;
}
